// react
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

// api
import {
  useGetBuildingUseListBuildingUseListGetQuery,
  useGetHeatInstallationListHeatInstallationListGetQuery,
} from '@/api/generated/energyAnalysis/endpoints/benchmarking';
import { useUpdatePortfolioAnalysisNowUpdatePortfolioAnalysisNowAccountIdPutMutation } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';
import {
  useGetSingleMatrikelMetadataMatrikelMetadataAccountIdMatrikelKeyGetQuery,
  useMatrikelOverwriteMatrikelOverwriteAccountIdMatrikelKeyPatchMutation,
} from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMatrikel';

// shared components
// shared-components
import { CancelButton, SubmitButton } from '@/components/ui/button';
import { RHFNumberField, RHFSelect } from '@/components/ui/form';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import {
  toBBRFormValues,
  toEditMatrikelBuildingFormValues,
  toMatrikelOverwritePatchInput,
} from './editMatrikelBuildingForm.mappers';
import { createEditMatrikelBuildingFormSchema } from './editMatrikelBuildingForm.schema';

import { zodResolver } from '@hookform/resolvers/zod';

interface EditMatrikelBuildingFormProps {
  accountId: number;
  matrikelKey: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

export default function EditMatrikelBuildingForm({
  accountId,
  matrikelKey,
  onSuccess,
  onCancel,
  onDirtyChange,
}: EditMatrikelBuildingFormProps) {
  const { t } = useTranslation(['forms', 'validation', 'common']);
  const dispatch = useDispatch();

  const { data: metadata, isLoading: isLoadingMetadata } =
    useGetSingleMatrikelMetadataMatrikelMetadataAccountIdMatrikelKeyGetQuery({
      accountId,
      matrikelKey,
    });

  const { data: buildingUseList } =
    useGetBuildingUseListBuildingUseListGetQuery();
  const { data: heatInstallationList } =
    useGetHeatInstallationListHeatInstallationListGetQuery();

  const [matrikelOverwrite, { isLoading: isSaving }] =
    useMatrikelOverwriteMatrikelOverwriteAccountIdMatrikelKeyPatchMutation();
  const [updatePortfolioAnalysis] =
    useUpdatePortfolioAnalysisNowUpdatePortfolioAnalysisNowAccountIdPutMutation();

  const bbrValues = toBBRFormValues(metadata);

  const schema = createEditMatrikelBuildingFormSchema();
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: toEditMatrikelBuildingFormValues(metadata),
    values: metadata ? toEditMatrikelBuildingFormValues(metadata) : undefined,
    mode: 'onTouched',
  });

  const watchedValues = useWatch({ control });

  const bbrDiff = {
    averageBuildingYear:
      watchedValues.averageBuildingYear !== bbrValues.averageBuildingYear,
    sumBuildingArea:
      watchedValues.sumBuildingArea !== bbrValues.sumBuildingArea,
    extractedBuildingUse:
      watchedValues.extractedBuildingUse !== bbrValues.extractedBuildingUse,
    extractedHeatInstallation:
      watchedValues.extractedHeatInstallation !==
      bbrValues.extractedHeatInstallation,
  };

  const hasAnyBBRDiff = Object.values(bbrDiff).some(Boolean);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      await matrikelOverwrite({
        accountId,
        matrikelKey,
        matrikelOverwritePatchInput: toMatrikelOverwritePatchInput(values),
      }).unwrap();

      updatePortfolioAnalysis({ accountId });

      dispatch(
        showSnackbar({
          message: t('common.updateSuccess', { ns: 'forms' }),
          severity: 'success',
        }),
      );

      onSuccess?.();
    } catch {
      dispatch(
        showSnackbar({
          message: t('common.updateError', { ns: 'forms' }),
          severity: 'error',
        }),
      );
    }
  });

  const buildingUseOptions = [
    { value: '', label: t('inputLabel.select', { ns: 'common' }) },
    ...(buildingUseList ?? []).map((use) => ({ value: use, label: use })),
  ];

  const heatInstallationOptions = [
    { value: '', label: t('inputLabel.select', { ns: 'common' }) },
    ...(heatInstallationList ?? []).map((inst) => ({
      value: inst,
      label: inst,
    })),
  ];

  if (isLoadingMetadata) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <RHFNumberField
                  name="averageBuildingYear"
                  control={control}
                  label={t('editMatrikelBuilding.averageBuildingYear.label', {
                    ns: 'forms',
                  })}
                  helperText={t(
                    'editMatrikelBuilding.averageBuildingYear.helperText',
                    { ns: 'forms' },
                  )}
                  fullWidth
                />
                {bbrDiff.averageBuildingYear && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color="warning"
                    label={
                      bbrValues.averageBuildingYear == null
                        ? t('editMatrikelBuilding.bbrNotSet', { ns: 'forms' })
                        : t('editMatrikelBuilding.bbrOriginalValue', {
                            ns: 'forms',
                            value: bbrValues.averageBuildingYear,
                          })
                    }
                    sx={{ mt: 0.5 }}
                  />
                )}
              </Box>
              <Box>
                <RHFNumberField
                  name="sumBuildingArea"
                  control={control}
                  label={t('editMatrikelBuilding.sumBuildingArea.label', {
                    ns: 'forms',
                  })}
                  helperText={t(
                    'editMatrikelBuilding.sumBuildingArea.helperText',
                    { ns: 'forms' },
                  )}
                  fullWidth
                />
                {bbrDiff.sumBuildingArea && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color="warning"
                    label={
                      bbrValues.sumBuildingArea == null
                        ? t('editMatrikelBuilding.bbrNotSet', { ns: 'forms' })
                        : t('editMatrikelBuilding.bbrOriginalValue', {
                            ns: 'forms',
                            value: bbrValues.sumBuildingArea,
                          })
                    }
                    sx={{ mt: 0.5 }}
                  />
                )}
              </Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('editMatrikelBuilding.buildingUseSection', { ns: 'forms' })}
              </Typography>
              <Box>
                <RHFSelect
                  name="extractedBuildingUse"
                  control={control}
                  label={t('editMatrikelBuilding.extractedBuildingUse.label', {
                    ns: 'forms',
                  })}
                  helperText={t(
                    'editMatrikelBuilding.extractedBuildingUse.helperText',
                    { ns: 'forms' },
                  )}
                  options={buildingUseOptions}
                  fullWidth
                />
                {bbrDiff.extractedBuildingUse && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color="warning"
                    label={
                      !bbrValues.extractedBuildingUse
                        ? t('editMatrikelBuilding.bbrNotSet', { ns: 'forms' })
                        : t('editMatrikelBuilding.bbrOriginalValue', {
                            ns: 'forms',
                            value: bbrValues.extractedBuildingUse,
                          })
                    }
                    sx={{ mt: 0.5 }}
                  />
                )}
              </Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('editMatrikelBuilding.heatInstallationSection', {
                  ns: 'forms',
                })}
              </Typography>
              <Box>
                <RHFSelect
                  name="extractedHeatInstallation"
                  control={control}
                  label={t(
                    'editMatrikelBuilding.extractedHeatInstallation.label',
                    { ns: 'forms' },
                  )}
                  helperText={t(
                    'editMatrikelBuilding.extractedHeatInstallation.helperText',
                    { ns: 'forms' },
                  )}
                  options={heatInstallationOptions}
                  fullWidth
                />
                {bbrDiff.extractedHeatInstallation && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color="warning"
                    label={
                      !bbrValues.extractedHeatInstallation
                        ? t('editMatrikelBuilding.bbrNotSet', { ns: 'forms' })
                        : t('editMatrikelBuilding.bbrOriginalValue', {
                            ns: 'forms',
                            value: bbrValues.extractedHeatInstallation,
                          })
                    }
                    sx={{ mt: 0.5 }}
                  />
                )}
              </Box>
              {/* <RHFCheckbox
                name="verificationFlag"
                control={control}
                label={t('editMatrikelBuilding.verificationFlag.label', {
                  ns: 'forms',
                })}
              /> */}
            </Stack>
          </CardContent>
        </Card>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <Box>
            {hasAnyBBRDiff && (
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={() => reset(bbrValues, { keepDefaultValues: true })}
                disabled={isSaving}
              >
                {t('editMatrikelBuilding.resetToBBR', { ns: 'forms' })}
              </Button>
            )}
          </Box>
          <Box display="flex" gap={1}>
            {onCancel && (
              <CancelButton onClick={onCancel} disabled={isSaving}>
                {t('common.cancel', { ns: 'forms' })}
              </CancelButton>
            )}
            <SubmitButton
              loading={isSaving}
              disabled={!isValid || !isDirty || isSaving}
            >
              {t('common.save', { ns: 'forms' })}
            </SubmitButton>
          </Box>
        </Box>
      </Stack>
    </form>
  );
}
